import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation, useMatches } from 'react-router-dom'
import * as HoverCardPrimitive from '@radix-ui/react-hover-card'

import useMediaQuery from '@/hooks/useMediaQuery'
import { cn } from '@/lib/utils'
import { RoutePath } from '@/routes'

import { Flex } from '../common/Box'
import { Exo2Text, Text } from '../common/Text'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../ui/accordion'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card'

const Navs: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const { gtXL } = useMediaQuery()
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const matches = useMatches()
  const currentRoute: any = matches.slice(-1)[0];

  const navs = useMemo(
    () => [
      // {
      //   title: t('nav.home'),
      //   path: RoutePath.HOME
      // },
      {
        title: t('nav.airdrop'),
        path: RoutePath.AIRDROP,
      },
      {
        title: t('nav.mining.influence'),
        path: RoutePath.INFLUENCE,
      },
      {
        title: t('nav.referral'),
        path: RoutePath.REFERRAL,
      },
      // {
      //   title: t('nav.earn'),
      //   path: RoutePath.MINING,
      //   children: [
      //     {
      //       title: t('nav.staking'),
      //       path: RoutePath.STAKING
      //     },
      //     {
      //       title: t('nav.mining'),
      //       path: RoutePath.MINING_PLATFORM
      //     },
      //     {
      //       title: t('nav.mining.influence'),
      //       path: RoutePath.MINING_INFLUENCE
      //     }
      //   ]
      // },
      // {
      //   title: t('nav.proposals'),
      //   path: RoutePath.PROPOSAL
      // }
    ],
    [t]
  )

  const renderNavChildren = useCallback(
    (children: (typeof navs)[number]['children'] = []) =>
      children.map((child) => (
        <NavLink
          key={child.path}
          to={child.path}
          className={cn(
            'mx-20 border-b last:border-none xl:mx-0 xl:border-none',
            'block rounded-md p-2 text-left leading-none hover:bg-primary',
            pathname === child.path && 'bg-primary'
          )}
          onClick={() => {
            onNavigate?.()
          }}
        >
          {child.title}
        </NavLink>
      )),
    [onNavigate, pathname]
  )

  return (
    <Flex
      className={cn(
        'flex-col text-center xl:flex-row xl:items-center xl:justify-center',
        'text-base xl:text-base',
        'h-full'
      )}
    >
      {navs.map((nav) =>
        nav.children ? (
          gtXL ? (
            <HoverCard key={nav.path} openDelay={0}>
              <HoverCardTrigger className="h-full">
                <Text
                  className={cn(
                    'flex h-full items-center',
                    'relative cursor-pointer border-b px-2 xl:border-none',
                    nav.children
                      .map(({ path }) => path)
                      .includes(pathname as RoutePath)
                      ? 'after:nav-active bg-primary/20 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1.5'
                      : 'hover:text-primary'
                  )}
                >
                  {nav.title}
                </Text>
              </HoverCardTrigger>
              <HoverCardPrimitive.Portal>
                <HoverCardContent
                  className="grid w-auto gap-1 border-white/10 bg-white/5 p-2 backdrop-blur-md"
                  align="start"
                >
                  {renderNavChildren(nav.children)}
                </HoverCardContent>
              </HoverCardPrimitive.Portal>
            </HoverCard>
          ) : (
            <Accordion key={nav.path} type="single" collapsible>
              <AccordionItem value={nav.path}>
                <AccordionTrigger
                  className={cn('border-none p-0 !no-underline')}
                >
                  <Exo2Text
                    className={cn(
                      '-mr-4 w-full cursor-pointer text-center',
                      'py-4 xl:py-0',
                      pathname.includes(nav.path) &&
                        'text-primary underline underline-offset-4'
                    )}
                  >
                    {nav.title}
                  </Exo2Text>
                </AccordionTrigger>
                <AccordionContent>
                  {renderNavChildren(nav.children)}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )
        ) : (
          <NavLink
            key={nav.path}
            to={nav.path}
            className={cn(
              'relative cursor-pointer border-b last:border-none xl:border-none',
              'flex h-full items-center px-2 py-4 xl:py-0',
              // pathname === nav.path
              currentRoute?.pathname === nav.path || currentRoute?.handle?.parent === nav.path
                ? 'after:nav-active bg-primary/20 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1.5'
                : 'hover:text-primary'
            )}
            onClick={() => {
              onNavigate?.()
            }}
          >
            {nav.title}
          </NavLink>
        )
      )}
    </Flex>
  )
}

export default Navs
